import { observable } from 'mobx';

import { SerializedData, FetchOptions } from 'bernie-core';
import { Store } from 'bernie-plugin-mobx';
import {
  AddOnRequested,
  FlightsQueryState,
  FlightsSearchFilterValuesInput,
  FlightsShoppingPwaFlightInformationResults,
  FlightsShoppingPwaFlightInformationResultsLoading,
  FlightsTripType,
  InsuranceCriteriaInput,
  SideSheetQueryContextInput
} from '__generated__/api/types';
import JourneyStartEndDates = FlightsShoppingPwaFlightInformationResultsLoading.JourneyStartEndDates;
import { getAddonsRequested } from 'experiments/Air_Smart_Shopping_44809';

export class InformationCriteriaStore extends Store {
  @observable public informationCriteria: FlightsShoppingPwaFlightInformationResults.Variables;
  @observable public sideSheetQueryContext: SideSheetQueryContextInput;
  @observable public informationFiltersValuesList: FlightsSearchFilterValuesInput[];
  @observable public sideSheetTempValues: {
    addOnRequested: AddOnRequested[];
    informationFiltersValuesList: FlightsSearchFilterValuesInput[];
  };
  @observable public insuranceCriteria: InsuranceCriteriaInput;

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public setInformationCriteria(informationCriteria: FlightsShoppingPwaFlightInformationResults.Variables) {
    if (informationCriteria) {
      this.informationCriteria = { ...informationCriteria };
    }
  }

  public setCriteriaFromUrl(fetchOptions: FetchOptions) {
    const query = fetchOptions.query;
    const journeyContinuationId = query?.journeyContinuationId;
    const originalBookingId = query?.originalBookingId;
    const ancillaryId = query?.ancillaryId;
    const upsellOfferToken = query?.upsellOfferToken;
    const client = query?.client;
    const opaqueToken = query?.opaqueToken;
    const itineraryNumber = query?.itineraryNumber;
    const addOnFilters = query?.addOnFilters ? JSON.parse(query.addOnFilters) : [];
    const addOnsRequested = addOnFilters ? getAddonsRequested(addOnFilters) : [];
    const insuranceCriteriaProductId = query?.insurProductId;

    this.informationCriteria.flightsDetailCriteria.journeyContinuationId = journeyContinuationId;
    this.sideSheetQueryContext.journeyContinuationId = journeyContinuationId;
    this.informationCriteria.flightsDetailCriteria.upsellOfferToken = upsellOfferToken;
    this.informationCriteria.flightsDetailCriteria.originalBookingId = originalBookingId;
    this.informationFiltersValuesList = addOnFilters;
    this.sideSheetQueryContext.addOnRequested = addOnsRequested;
    this.sideSheetTempValues.informationFiltersValuesList = addOnFilters;
    this.sideSheetTempValues.addOnRequested = addOnsRequested;
    this.insuranceCriteria.selectedProductId = insuranceCriteriaProductId;

    if (!fetchOptions.isServer && ancillaryId) {
      this.informationCriteria.flightsDetailCriteria.ancillaryId = ancillaryId;
    }
    if (client === 'virtual-agent' && opaqueToken && itineraryNumber) {
      this.informationCriteria.virtualAgentContext = {
        opaqueToken: opaqueToken,
        itineraryNumber: itineraryNumber
      };
    }
  }

  public frameJourneyCriteriasFIS(
    regionIds: string[] | string,
    journeyStartEndDates: JourneyStartEndDates[],
    journeyTripType?: string
  ) {
    if (journeyStartEndDates === null) {
      journeyStartEndDates = [{ __typename: 'JourneyStartEndDates', startDate: '', endDate: '' }];
    }

    const tripType = InformationCriteriaStore.getTripType(journeyTripType);
    const { startDate, endDate } = journeyStartEndDates[0];

    if (regionIds && regionIds !== null) {
      const journeyCriterias = [];
      if (typeof regionIds === 'string') {
        journeyCriterias.push({
          destination: {
            regionId: regionIds
          },
          ...(startDate &&
            endDate && {
              dateRange: {
                start: startDate,
                end: endDate
              }
            }),
          ...(tripType && { tripType })
        });
      } else {
        const numberOfDestinations = regionIds.length;
        const lastEndDate = journeyStartEndDates[journeyStartEndDates.length - 1].endDate;
        for (let i = 0; i < numberOfDestinations; i++) {
          journeyCriterias.push({
            destination: {
              regionId: regionIds[i]
            },
            ...(startDate &&
              lastEndDate && {
                dateRange: {
                  start: startDate,
                  end: lastEndDate
                }
              }),
            ...(tripType && { tripType })
          });
        }
      }

      return journeyCriterias;
    }
  }

  private static getTripType(type?: string) {
    if (!type) {
      return '';
    }

    switch (type.toLowerCase()) {
      case 'roundtrip':
        return FlightsTripType.ROUND_TRIP;
      case 'oneway':
        return FlightsTripType.ONE_WAY;
      case 'multi':
        return FlightsTripType.MULTI_DESTINATION;
      default:
        return '';
    }
  }

  public setJourneyContinuationId(jcid: string) {
    this.informationCriteria.flightsDetailCriteria.journeyContinuationId = jcid;
    this.sideSheetQueryContext.journeyContinuationId = jcid;
  }

  public setInsuranceCriteria(insuranceCriteria?: InsuranceCriteriaInput) {
    this.insuranceCriteria = insuranceCriteria;
  }
}

InformationCriteriaStore.DEFAULTS = {
  informationCriteria: {
    queryState: FlightsQueryState.LOADING,
    flightsDetailCriteria: { journeyContinuationId: 'test', ancillaryId: undefined }
  },
  insuranceCriteria: {
    selectedProductId: undefined
  },
  sideSheetQueryContext: {
    addOnRequested: [],
    isFirstLoad: true,
    journeyContinuationId: 'test'
  },
  informationFiltersValuesList: [],
  sideSheetTempValues: {
    addOnRequested: [],
    informationFiltersValuesList: []
  }
};
